import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { db } from "@/modules/shared/lib/db-client";
import { toast } from "sonner";
import { reportError } from "@/modules/shared/lib/observability";

export type AiProvider = "openai" | "google" | "anthropic" | "deepseek" | "custom";

export interface AssistantSettings {
  id: string;
  organization_id: string;
  is_enabled: boolean;
  assistant_name: string;
  assistant_subtitle: string | null;
  logo_url: string | null;
  primary_color: string;
  model: string;
  system_prompt: string | null;
  suggestions: string[];
  ai_provider: AiProvider;
  custom_api_key: string | null;
  custom_model_id: string | null;
  custom_endpoint_url: string | null;
  perm_read_data: boolean;
  perm_create_records: boolean;
  perm_update_records: boolean;
  perm_financial_access: boolean;
  denial_message: string | null;
  greeting_message: string | null;
  greeting_description: string | null;
  input_placeholder: string | null;
  error_message: string | null;
}

const DEFAULTS: Omit<AssistantSettings, "id" | "organization_id"> = {
  is_enabled: true,
  assistant_name: "Assistente IA",
  assistant_subtitle: "Agente inteligente",
  logo_url: null,
  primary_color: "#10b981",
  model: "gpt-4o-mini",
  system_prompt: "Você é um assistente inteligente da plataforma. Ajude os usuários com consultas de dados, registros e tarefas operacionais. Seja claro, objetivo e amigável.",
  suggestions: ["Como faço um check-in?", "Buscar profissionais disponíveis", "Quais alocações estão ativas?", "Consultar metas do trimestre"],
  ai_provider: "openai",
  custom_api_key: null,
  custom_model_id: null,
  custom_endpoint_url: null,
  perm_read_data: true,
  perm_create_records: true,
  perm_update_records: true,
  perm_financial_access: false,
  denial_message: "Desculpe, {name}. Eu não posso fazer isso.",
  greeting_message: "Olá! Sou o {name}",
  greeting_description: "Posso consultar dados, registrar sessões 1:1, avaliações e muito mais. Envie um arquivo ou pergunte!",
  input_placeholder: "Digite ou cole uma ata...",
  error_message: "Desculpe, ocorreu um erro. Tente novamente.",
};

export function useAssistantSettings() {
  const queryClient = useQueryClient();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["assistant-settings"],
    queryFn: async () => {
      const { data, error } = await db
        .from("settings")
        .select("*")
        .eq("scope", "assistant")
        .eq("scope_key", "_default")
        .maybeSingle();

      if (error) throw error;
      if (!data) return { ...DEFAULTS, id: "", organization_id: "" } as AssistantSettings;

      const config = data.config as Record<string, any>;
      return {
        id: data.id,
        organization_id: data.organization_id,
        ...config,
        suggestions: Array.isArray(config.suggestions) ? config.suggestions : DEFAULTS.suggestions,
      } as AssistantSettings;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<AssistantSettings>) => {
      const { data: existing } = await db
        .from("settings")
        .select("id, config")
        .eq("scope", "assistant")
        .eq("scope_key", "_default")
        .maybeSingle();

      const cleanUpdates = { ...updates };
      delete (cleanUpdates as any).id;
      delete (cleanUpdates as any).organization_id;

      if (existing?.id) {
        const config = { ...(existing.config as Record<string, any>), ...cleanUpdates };
        const { error } = await db
          .from("settings")
          .update({ config } as any)
          .eq("id", existing.id);
        if (error) throw error;
      } else {
        const { error } = await db
          .from("settings")
          .insert({ scope: "assistant", scope_key: "_default", config: cleanUpdates } as any);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["assistant-settings"] });
      toast.success("Configurações do assistente salvas!");
    },
    onError: (err) => {
      reportError(err, "update-assistant-settings");
      toast.error("Erro ao salvar configurações: " + (err as Error).message);
    },
  });

  return { settings: settings || (DEFAULTS as any as AssistantSettings), isLoading, updateSettings };
}
